import { isDefined } from "@omni/utils/helper.utils";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { useWoosmapScript } from "@/services/woosmap/woosmap.initializer";
import { DamImage } from "@/utils/dam.utils";
import { ScriptLoadingStates } from "@/utils/hooks/useScript.hook";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import { fetchRoutes } from "../helpers/woosmap.helpers";
import { ZOOM_INITIAL } from "../storeDirections.config";
import { useMap } from "./useMap.hook";

type UseDirectionsProps = {
    origin?: woosmap.map.LatLngLiteral;
    destination: woosmap.map.LatLngLiteral;
    mapElementRef: RefObject<HTMLDivElement | null>;
};

export const useDirections = ({
    origin,
    destination,
    mapElementRef
}: UseDirectionsProps) => {
    const woosmapScriptLoadingState = useWoosmapScript();
    const ready = woosmapScriptLoadingState === ScriptLoadingStates.Ready;

    const map = useMap(mapElementRef, destination);

    const { DirectionsService } = useWoosmapServices();

    const directionsRendererRef = useRef<woosmap.map.DirectionsRenderer | null>(
        null
    );

    const originMarkerRef = useRef<woosmap.map.Marker | null>(null);

    const [route, setRoute] = useState<woosmap.map.DirectionRoute | undefined>(
        undefined
    );

    useEffect(() => {
        if (!ready) return;

        if (!isDefined(directionsRendererRef.current)) {
            directionsRendererRef.current = new woosmap.map.DirectionsRenderer({
                preserveViewport: true
            });
        }

        if (!isDefined(originMarkerRef.current)) {
            originMarkerRef.current = new woosmap.map.Marker();
        }
    }, [ready]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const renderDirections = useCallback(
        (origin: woosmap.map.LatLngLiteral) => {
            if (!isDefined(DirectionsService)) return;

            return fetchRoutes(
                {
                    origin: origin,
                    destination: destination
                },
                DirectionsService
            )
                .then((directions) => {
                    directionsRendererRef.current?.setMap(map ?? null);
                    directionsRendererRef.current?.setDirections(directions);

                    return directions.routes.at(0);
                })
                .then((route) => {
                    setRoute(route);

                    if (isDefined(route)) {
                        const bounds = new woosmap.map.LatLngBounds(
                            route.bounds.northeast,
                            route.bounds.southwest
                        );
                        const padding: woosmap.map.Padding = {
                            top: 210,
                            bottom: 32,
                            left: 32,
                            right: 32
                        };

                        map?.fitBounds(bounds, padding, true);
                    }
                });
        },
        [
            destination.lat,
            destination.lng,
            directionsRendererRef,
            map,
            DirectionsService
        ]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const clearDirections = useCallback(
        () =>
            directionsRendererRef.current?.setDirections({
                routes: [],
                status: "OK"
            }),
        [directionsRendererRef]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const renderOriginMarker = useCallback(
        (origin: woosmap.map.LatLngLiteral) => {
            originMarkerRef.current?.setIcon({
                url: DamImage.icon.maps.markerDot(),
                scaledSize: {
                    height: 15,
                    width: 15
                }
            });
            originMarkerRef.current?.setPosition(origin);
            originMarkerRef.current?.setMap(map);
        },
        [originMarkerRef, map]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const clearOriginMarker = useCallback(
        () => originMarkerRef.current?.setMap(null),
        [originMarkerRef]
    );

    const resetMapCenter = useCallback(() => {
        map?.flyTo({
            center: destination,
            zoom: ZOOM_INITIAL
        });
    }, [map, destination]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!ready) return;

        if (isDefined(origin)) {
            renderDirections(origin);
            renderOriginMarker(origin);
        } else {
            clearDirections();
            clearOriginMarker();
            resetMapCenter();
        }
    }, [
        ready,
        renderDirections,
        clearDirections,
        renderOriginMarker,
        clearOriginMarker,
        resetMapCenter,
        origin?.lat,
        origin?.lat
    ]);

    return route;
};
